import type { IconType, UseModalState } from "@clipboard-health/ui-components";
import { type TimeSlot } from "@src/appV2/redesign/Shift/Open/types";
import type { WorkerShift } from "@src/appV2/Shifts/Shift/types";
import { z } from "zod";

import { QuestionTag, ReviewQuestionType } from "../redesign/Workplace/Review/types";

export type PostShiftReviewStep =
  | "compare-workplaces"
  | "peer-reviews"
  | "rate-workplace-questions"
  | "pick-rating"
  | "write-review"
  | "choose-anonymity"
  | QuestionTag.SOFTWARE
  | QuestionTag.SUPPLIES
  | QuestionTag.PATIENTS_PER_NURSE
  | QuestionTag.PARKING
  | "end";

export interface RatingQuestionInfo {
  questionId: string;
  questionText: string;
  tag: QuestionTag;
}

export interface WriteReviewStepData {
  reviewText: string;
}

export interface SoftwareQuestionStepData {
  answer: string;
  otherText?: string;
}

export interface SuppliesQuestionStepData {
  answer: string;
}

export interface PatientCountQuestionStepData {
  answer: string;
}

export interface ParkingQuestionStepData {
  parkingAnswer: string | undefined;
}

export interface ChooseAnonymityStepData {
  isAnonymous: boolean;
}

export type ComparisonSelection = "workplaceA" | "workplaceB" | "tie";

export interface CompareWorkplacesStepData {
  comparisonSelection: ComparisonSelection;
}

// Union type for all possible step data
export type StepActionData =
  | WriteReviewStepData
  | SoftwareQuestionStepData
  | SuppliesQuestionStepData
  | PatientCountQuestionStepData
  | ParkingQuestionStepData
  | ChooseAnonymityStepData
  | CompareWorkplacesStepData;

export interface StepAction {
  type: "SUBMIT_RATING" | "NEXT_STEP" | "BACK_STEP" | "SKIP_STEP" | "CLOSE_FLOW" | "SUBMIT_REVIEW";
  rating?: number;
  data?: StepActionData;
}
// FIXME: Automatically disabled during ESLint config update
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { QuestionTag } from "../redesign/Workplace/Review/types";

export const QUESTION_TAG_ICON_MAP = {
  [QuestionTag.SOFTWARE]: "software",
  [QuestionTag.SUPPLIES]: "supplies",
  [QuestionTag.PATIENTS_PER_NURSE]: "patients",
  [QuestionTag.PARKING]: "car",
  [QuestionTag.CLEANLINESS]: "new",
  [QuestionTag.PATIENT_CARE]: "patients",
  [QuestionTag.CULTURE]: "user-group",
  [QuestionTag.SUPPLY_RATING]: "supplies",
} as const;

export const DEFAULT_QUESTION_TAG_ICON = "info" as const;

export interface QuestionData {
  tags?: Array<{ tag: QuestionTag }>;
}

export interface QuestionsResponse {
  data: QuestionData[];
}

export interface PostShiftReviewFlowProps {
  modalState: UseModalState;
  facility: {
    id: string;
    name: string;
    type: string;
    photo?: string;
    address?: string;
  };
  shiftId?: string;
  onComplete: () => void;
  isDelayedReview?: boolean;
}

export interface NumberQuestionConfig {
  minValue: number;
  maxValue: number;
}

export interface ConsolidatedQuestionItem {
  tag: QuestionTag;
  questionId: string;
  questionText: string;
  questionType: ReviewQuestionType;
  numberConfig?: NumberQuestionConfig;
  iconType: IconType;
}

export interface QuestionMappings {
  commentQuestionId: string | undefined;
  tagToQuestionId: Partial<Record<QuestionTag, string>>;
  tagToQuestionText: Partial<Record<QuestionTag, string>>;
  questionOptions: Record<string, Array<{ id: string; displayText: string }>>;
  questionNumberConfig: Record<string, NumberQuestionConfig>;
  questionTypes: Record<string, ReviewQuestionType>;
  ratingQuestions: RatingQuestionInfo[];
  orderedTags?: QuestionTag[];
}

export const postShiftReviewFormSchema = z.object({
  rating: z.number().min(1, "Please rate your shift").optional(),
  isAnonymous: z.boolean().optional(), // Allow unselected state initially
  reviewText: z.string(),
  softwareAnswer: z.string().optional(),
  softwareOtherText: z.string().optional(),
  suppliesAnswer: z.string().optional(),
  patientCountAnswer: z.string().optional(),
  parkingAnswer: z.string().optional(),
  ratingAnswers: z.record(z.string(), z.string()).optional(),
  comparisonSelection: z.enum(["workplaceA", "workplaceB", "tie"]).optional(),
});

export type PostShiftReviewFormValues = z.infer<typeof postShiftReviewFormSchema>;

export interface ComparisonPairWorkplace {
  workplaceId: string;
  workplaceName: string;
  address?: string;
  timeSlot?: TimeSlot;
  lastWorkedAt?: string;
}

export interface ComparisonPairData {
  workplaceA: ComparisonPairWorkplace;
  workplaceB: ComparisonPairWorkplace;
  shiftId: string | null;
}

export interface PostShiftReviewFlowContentProps extends PostShiftReviewFlowProps {
  questionMappings?: QuestionMappings;
  isLoading: boolean;
  isError: boolean;
  onSubmitRating?: (rating: number) => Promise<void>;
  onSubmitReview: (formData: PostShiftReviewFormValues) => Promise<void>;
  onSubmitReviewError: (error: unknown) => void;
  onSubmitComparison?: (selection: ComparisonSelection) => void;
  shift?: WorkerShift;
  comparisonPair?: ComparisonPairData;
}
